import { useState, useEffect } from 'react';
import api, { auditAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Settings,
  Shield,
  Database,
  FileSpreadsheet,
  Activity,
  Clock,
  User,
  Loader2,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  Link as LinkIcon,
  Unlink,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Google Integration State
  const [googleStatus, setGoogleStatus] = useState(null);
  const [loadingGoogleStatus, setLoadingGoogleStatus] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [sheetUrl, setSheetUrl] = useState('');
  const [linkedSheet, setLinkedSheet] = useState(null);

  useEffect(() => {
    if (activeTab === 'audit' && user?.role === 'admin') {
      fetchAuditLogs();
    }
    if (activeTab === 'integrations' && user?.role === 'admin') {
      fetchGoogleStatus();
    }
  }, [activeTab, user?.role]);

  const fetchAuditLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await auditAPI.getLogs({ limit: 50 });
      setAuditLogs(response.data);
    } catch (err) {
      toast.error('Failed to fetch audit logs');
    }
    setLoadingLogs(false);
  };

  const fetchGoogleStatus = async () => {
    setLoadingGoogleStatus(true);
    try {
      const response = await api.get('/integrations/google/status');
      setGoogleStatus(response.data);
      
      // Fetch linked sheet info if connected
      if (response.data.connected) {
        try {
          const sheetResponse = await api.get('/integrations/google/linked-sheet');
          setLinkedSheet(sheetResponse.data);
        } catch (err) {
          // No linked sheet yet
        }
      }
    } catch (err) {
      console.error('Failed to fetch Google status:', err);
      setGoogleStatus({
        configured: false,
        connected: false,
        client_id_set: false,
        client_secret_set: false
      });
    }
    setLoadingGoogleStatus(false);
  };

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const response = await api.get('/oauth/google/login');
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate Google connection');
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await api.post('/integrations/google/disconnect');
      toast.success('Google account disconnected');
      setGoogleStatus(prev => ({ ...prev, connected: false }));
      setLinkedSheet(null);
    } catch (err) {
      toast.error('Failed to disconnect Google account');
    }
  };

  const handleLinkSheet = async () => {
    if (!sheetUrl.trim()) {
      toast.error('Please enter a Google Sheet URL or ID');
      return;
    }

    // Extract sheet ID from URL if full URL provided
    let sheetId = sheetUrl.trim();
    const urlMatch = sheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (urlMatch) {
      sheetId = urlMatch[1];
    }

    try {
      const response = await api.post('/integrations/google/link-sheet', { sheet_id: sheetId });
      toast.success('Sheet linked successfully');
      setLinkedSheet(response.data);
      setSheetUrl('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link sheet');
    }
  };

  const handleUnlinkSheet = async () => {
    try {
      await api.post('/integrations/google/unlink-sheet');
      toast.success('Sheet unlinked');
      setLinkedSheet(null);
    } catch (err) {
      toast.error('Failed to unlink sheet');
    }
  };

  const handleSyncToSheets = async () => {
    if (!linkedSheet) {
      toast.error('No sheet linked');
      return;
    }

    try {
      await api.post('/integrations/google/sync-patients');
      toast.success('Patients synced to Google Sheets');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync');
    }
  };

  const getActionColor = (action) => {
    if (action.includes('create')) return 'bg-green-100 text-green-800';
    if (action.includes('update')) return 'bg-blue-100 text-blue-800';
    if (action.includes('delete')) return 'bg-red-100 text-red-800';
    if (action.includes('login')) return 'bg-purple-100 text-purple-800';
    return 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and system settings
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          {(user?.role === 'admin' || user?.role === 'physician') && (
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Logs</TabsTrigger>
          )}
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile
              </CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input value={user?.full_name || ''} disabled data-testid="profile-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled data-testid="profile-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <div>
                  <Badge className="capitalize">{user?.role}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security
              </CardTitle>
              <CardDescription>Security settings and session info</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Session Timeout</p>
                    <p className="text-sm text-muted-foreground">
                      Sessions automatically expire after 8 hours of inactivity
                    </p>
                  </div>
                </div>
                <Badge variant="secondary">8 hours</Badge>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Database className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Data Encryption</p>
                    <p className="text-sm text-muted-foreground">
                      All data is encrypted at rest and in transit
                    </p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-800">Enabled</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="space-y-6">
          {/* Google Integration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Google Sheets & Calendar
              </CardTitle>
              <CardDescription>
                Connect your Google account to sync patient data to Sheets and manage appointments in Calendar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {user?.role !== 'admin' ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Admin access required to manage integrations</p>
                </div>
              ) : loadingGoogleStatus ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : !googleStatus?.configured ? (
                /* Configuration Required */
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Setup Required</AlertTitle>
                  <AlertDescription className="mt-2">
                    <p className="mb-3">Google OAuth is not configured. To enable this integration:</p>
                    <ol className="list-decimal ml-4 space-y-1 text-sm">
                      <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                      <li>Create a new project or select an existing one</li>
                      <li>Enable the <strong>Google Sheets API</strong> and <strong>Google Calendar API</strong></li>
                      <li>Create OAuth 2.0 credentials (Web application type)</li>
                      <li>Add the following environment variables to your backend:</li>
                    </ol>
                    <div className="mt-3 p-3 bg-muted rounded-md font-mono text-xs space-y-1">
                      <p>GOOGLE_CLIENT_ID=your_client_id</p>
                      <p>GOOGLE_CLIENT_SECRET=your_client_secret</p>
                      <p>GOOGLE_REDIRECT_URI={window.location.origin}/api/oauth/google/callback</p>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Badge variant={googleStatus?.client_id_set ? "default" : "destructive"}>
                        {googleStatus?.client_id_set ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                        GOOGLE_CLIENT_ID
                      </Badge>
                      <Badge variant={googleStatus?.client_secret_set ? "default" : "destructive"}>
                        {googleStatus?.client_secret_set ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                        GOOGLE_CLIENT_SECRET
                      </Badge>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : !googleStatus?.connected ? (
                /* Not Connected - Show Connect Button */
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Connect Google Account</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Connect your Google account to enable syncing patient data to Google Sheets 
                    and managing appointments in Google Calendar.
                  </p>
                  <Button 
                    onClick={handleConnectGoogle}
                    disabled={connectingGoogle}
                    size="lg"
                    className="gap-2"
                    data-testid="connect-google-btn"
                  >
                    {connectingGoogle ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <LinkIcon className="h-4 w-4" />
                    )}
                    Connect Google Account
                  </Button>
                </div>
              ) : (
                /* Connected - Show Status and Options */
                <div className="space-y-6">
                  {/* Connection Status */}
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-900">Google Account Connected</p>
                        <p className="text-sm text-green-700">
                          {googleStatus.email || 'Connected'}
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleDisconnectGoogle}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>

                  <Separator />

                  {/* Google Sheets Linking */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      Google Sheets
                    </h4>
                    
                    {linkedSheet ? (
                      <div className="p-4 border rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{linkedSheet.title}</p>
                            <p className="text-sm text-muted-foreground">
                              ID: <code className="text-xs bg-muted px-1 py-0.5 rounded">{linkedSheet.id}</code>
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`https://docs.google.com/spreadsheets/d/${linkedSheet.id}`, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Open
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleUnlinkSheet}
                            >
                              <Unlink className="h-4 w-4 mr-2" />
                              Unlink
                            </Button>
                          </div>
                        </div>
                        <Button onClick={handleSyncToSheets} className="w-full">
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Sync Patients to Sheet
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Enter a Google Sheet URL or ID to link it with the EMR for data sync.
                        </p>
                        <div className="flex gap-2">
                          <Input
                            placeholder="https://docs.google.com/spreadsheets/d/... or Sheet ID"
                            value={sheetUrl}
                            onChange={(e) => setSheetUrl(e.target.value)}
                            className="flex-1"
                            data-testid="sheet-url-input"
                          />
                          <Button onClick={handleLinkSheet}>
                            <LinkIcon className="h-4 w-4 mr-2" />
                            Link Sheet
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Google Calendar Status */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Google Calendar
                    </h4>
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Calendar sync is available. Appointments can be synced to your Google Calendar.</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Other Integrations */}
          <Card>
            <CardHeader>
              <CardTitle>Other Integrations</CardTitle>
              <CardDescription>Additional integration options</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {[
                  { name: 'Patient Portal', status: 'Coming Soon', icon: '🏥' },
                  { name: 'Analytics Dashboard', status: 'Coming Soon', icon: '📊' },
                  { name: 'HL7/FHIR Integration', status: 'Coming Soon', icon: '🔗' },
                  { name: 'Telehealth', status: 'Coming Soon', icon: '📹' },
                ].map(integration => (
                  <div key={integration.name} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{integration.icon}</span>
                      <span className="font-medium">{integration.name}</span>
                    </div>
                    <Badge variant="outline">{integration.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs */}
        <TabsContent value="audit">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Audit Logs
                </CardTitle>
                <CardDescription>System activity and user actions</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchAuditLogs} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loadingLogs ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
                  <p className="text-muted-foreground">No audit logs found</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Action</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead className="hidden md:table-cell">User</TableHead>
                        <TableHead>Timestamp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log) => (
                        <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                          <TableCell>
                            <Badge className={getActionColor(log.action)}>
                              {log.action.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {log.entity_type}
                            </code>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {log.user_email}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(log.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
